/* eslint-disable no-useless-return */
import type { RootState } from 'core/store';
import type { RewardProcessResponse } from 'core/services/bff';
import type { ContentData, DataValuesEmptyError } from 'core/domains';

import { noticeError } from 'core/utilities/helpers/notice-error';
import { getFeatureFlag, IS_HYBRID_CART_ACTIVE } from 'core/services';
import { history } from 'core/use-cases/utilities';
import { call, put, select } from 'core/middlewares/utilities';

import { app, cart, checkout, combo, content, user, utilities } from 'core/use-cases';
import { actions as actionsshippingModel } from 'core/use-cases/shippingModel';

import { getters } from './getters';
import { notification, messages, persistedExpiration } from './tasks';
import { checkoutInit } from './checkout-init';
import { itemAndComboAdd } from './item-and-combo-add';
import { showNotificationMinimumOrderAmount } from './show-notification-minimum-order-amount';
import { getLocationPathname } from 'core/utilities';
import { IncentiveCampaigns } from 'core/domains/incentive-campaigns';

/**
 * Broker responsible for handling the success request flow.
 *
 */
export function* successFlow(action: utilities.ActionSuccess) {
  const successEventTypes = Object.keys(messages.success);
  const pathname = getLocationPathname();
  yield persistedExpiration(action);

  if (action.type === combo.actions.comboFetch.success.type) {
    if (!action?.payload?.available) {
      history.push(`/combo-error?q=${action?.payload?.comboCode}`);
    }
  }

  if (action.type === combo.actions.comboPost.success.type) {
    const homePage: getters.RoutePath = yield getters.getHomepageRoute();

    yield put(app.actions.setLoading(''));
    history.push(homePage);
    yield put(app.actions.toggleCart(true));
    yield notification.success(action);

    return;
  }

  if (successEventTypes.includes(action.type)) {
    yield notification.success(action);
    yield put(app.actions.setLoading(''));

    return;
  }

  if (
    action.type === cart.actions.cartFetch.success.type ||
    action.type === cart.actions.setCouponFetch.success.type ||
    action.type === cart.actions.lastCartFetch.success.type ||
    action.type === cart.actions.clearCouponFetch.success.type
  ) {
    yield put(app.actions.setLoading(''));

    return;
  }

  if (action.type === cart.actions.cartFetchForGiftsCheck.success.type) {
    yield put(app.actions.setLoading(''));

    const isMinimumOrder: boolean = yield select(({ cart }: RootState) => cart.canSubmit);

    if (!isMinimumOrder) {
      yield put(app.actions.toggleCart(true));

      yield showNotificationMinimumOrderAmount();

      return;
    }

    yield put(checkout.actions.processFetch.request());

    return;
  }

  if (action.type === checkout.actions.processFetch.success.type) {
    const { choose = [] } = action.payload as RewardProcessResponse['rewards']['data'];

    if (choose.length) {
      history.push(`/checkout/brindes`);
    } else {
      if (pathname !== '/checkout') {
        history.push(`/checkout`);
      }

      yield checkoutInit();
    }

    yield put(app.actions.toggleCart(false));
    yield put(app.actions.setLoading(''));

    return;
  }

  if (action.type === checkout.actions.promotionFetch.success.type) {
    history.push(`/checkout`);
    yield put(app.actions.setLoading(''));

    return;
  }

  if (
    action.type === content.actions.starlinkPageFetch.success.type ||
    action.type === content.actions.starlinkContentFetch.success.type
  ) {
    const { payload } = action as unknown as { payload: ContentData };
    const resellerId: RootState['user']['data']['resellerId'] = yield select(
      ({ user }: RootState) => user.data.resellerId,
    );

    const components = payload?.components;

    if (components && components.length === 0) {
      const data = {
        resellerId,
        detail: 'BFF returned components empty',
      };
      noticeError<DataValuesEmptyError>('segmentation-assets', data);
    }
  }

  if (
    action.type === actionsshippingModel.currentDeliveryModeFetch.success.type ||
    action.type === cart.actions.cartSetShippingModelFetch.success.type
  ) {
    yield put(app.actions.setLoading(''));

    const resellerId: RootState['user']['data']['resellerId'] = yield select(
      (rootState: RootState) => rootState.user.data.resellerId,
    );

    const isFeatureFlagActice: boolean = yield call(getFeatureFlag, {
      flag: IS_HYBRID_CART_ACTIVE,
      defaultValue: false,
      resellerId,
    });

    if (
      isFeatureFlagActice &&
      action.type === actionsshippingModel.currentDeliveryModeFetch.success.type
    ) {
      yield put(cart.actions.cartCurrentDeliveryFetch.request());
    } else {
      yield itemAndComboAdd();
    }
  }

  if (action.type === cart.actions.cartCurrentDeliveryFetch.success.type) {
    yield itemAndComboAdd();
  }

  if (action.type === user.actions.changeCycle.success.type) {
    window.dataLayer.push({
      event: 'analytics-event',
      event_parameters: {
        event_name: 'callback_atualizar_ciclo_sucesso',
      },
    });
  }

  if (action.type === user.actions.incentiveCampaignsListFetch.success.type) {
    const { payload } = action as unknown as { payload: IncentiveCampaigns };
    const data = payload?.data;

    return data;
  }
}
